/**
 * Product Carousel
 * High-performance, vanilla JS carousel for PDP media
 */

class ProductCarousel {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.sectionId = container.dataset.sectionId;

    const dataEl = document.getElementById(`carousel-data-${this.sectionId}`);
    if (!dataEl) return;

    try {
      this.config = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('ProductCarousel: Failed to parse config', e);
      return;
    }

    // DOM references
    this.track = container.querySelector('[data-track]');
    this.slides = Array.from(container.querySelectorAll('.product-carousel__slide'));
    this.filmstrip = container.querySelector('.product-carousel__filmstrip');
    this.filmstripTrack = container.querySelector('.product-carousel__filmstrip-track');
    this.filmstripThumbs = Array.from(container.querySelectorAll('.product-carousel__thumb'));
    this.dots = Array.from(container.querySelectorAll('.product-carousel__dot'));
    this.miniThumbs = Array.from(container.querySelectorAll('.product-carousel__mini-thumb'));
    this.liveRegion = container.querySelector('.product-carousel__live-region');

    // State
    this.currentIndex = 0;
    this.mediaCount = this.slides.length;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.isSwiping = false;
    this.swipeDirection = null; // 'horizontal', 'vertical', or null
    this.dragThreshold = 40;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._init();
  }

  _init() {
    // Set initial slide
    const initialIndex = this.slides.findIndex(
      slide => parseInt(slide.dataset.mediaId) === this.config.initialMediaId
    );
    if (initialIndex > -1) this.currentIndex = initialIndex;

    this._checkUrlVariant();
    this._updateTrackPosition(false);
    this._updateActiveStates(true); // true = skip scrollIntoView on init
    this._bindEvents();
    this._setupVideos();
    this._checkFilmstripOverflow();
  }

  _bindEvents() {
    // Touch handling - only touchmove needs passive:false for preventDefault
    this.track.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: false });
    this.track.addEventListener('touchend', this._onTouchEnd.bind(this), { passive: true });

    // Mouse for desktop
    this.track.addEventListener('mousedown', this._onMouseDown.bind(this));

    // Keyboard
    this.container.addEventListener('keydown', this._onKeyDown.bind(this));

    // Thumbnails
    this.filmstripThumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.goToSlide(parseInt(thumb.dataset.index)));
    });
    this.dots.forEach(dot => {
      dot.addEventListener('click', () => this.goToSlide(parseInt(dot.dataset.index)));
    });
    this.miniThumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.goToSlide(parseInt(thumb.dataset.index)));
    });

    // Variant changes
    document.addEventListener('variant:change', this._onVariantChange.bind(this));

    // Filmstrip scroll
    if (this.filmstripTrack) {
      this.filmstripTrack.addEventListener('scroll', this._debounce(() => {
        const isAtBottom = this.filmstripTrack.scrollHeight - this.filmstripTrack.scrollTop <= this.filmstripTrack.clientHeight + 5;
        this.filmstrip.classList.toggle('scrolled-to-bottom', isAtBottom);
      }, 100));
    }

    // Resize
    this._resizeObserver = new ResizeObserver(this._debounce(() => {
      this._checkFilmstripOverflow();
      this._updateTrackPosition(false);
    }, 100));
    this._resizeObserver.observe(this.container);

    // YouTube
    this.container.querySelectorAll('[data-youtube-play]').forEach(btn => {
      btn.addEventListener('click', (e) => this._playYouTube(e.currentTarget));
    });
  }

  // ===================
  // TOUCH HANDLING - Simplified
  // ===================
  _onTouchStart(e) {
    if (e.touches.length !== 1) return;

    const touch = e.touches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.currentX = touch.clientX;
    this.isSwiping = false;
    this.swipeDirection = null;
  }

  _onTouchMove(e) {
    if (e.touches.length !== 1) return;

    const touch = e.touches[0];
    const diffX = touch.clientX - this.startX;
    const diffY = touch.clientY - this.startY;
    const absDiffX = Math.abs(diffX);
    const absDiffY = Math.abs(diffY);

    // Determine direction on first significant move
    if (this.swipeDirection === null && (absDiffX > 5 || absDiffY > 5)) {
      if (absDiffX >= absDiffY) {
        this.swipeDirection = 'horizontal';
        this.isSwiping = true;
      } else {
        this.swipeDirection = 'vertical';
        this.track.classList.remove('is-dragging');
        return; // Let browser handle vertical scroll
      }
    }

    // If horizontal, prevent scroll and handle carousel
    if (this.swipeDirection === 'horizontal') {
      e.preventDefault();
      e.stopPropagation();

      this.currentX = touch.clientX;

      const baseOffset = -this.currentIndex * 100;
      let dragPercent = (diffX / this.track.offsetWidth) * 100;

      // Boundary resistance
      const atStart = this.currentIndex === 0 && diffX > 0;
      const atEnd = this.currentIndex === this.mediaCount - 1 && diffX < 0;

      if (atStart || atEnd) {
        dragPercent = dragPercent * 0.2;
      }

      this.track.style.transform = `translateX(${baseOffset + dragPercent}%)`;
    }
  }

  _onTouchEnd() {
    if (!this.isSwiping) {
      this.swipeDirection = null;
      return;
    }

    this.track.classList.remove('is-dragging');

    const diff = this.currentX - this.startX;

    if (Math.abs(diff) > this.dragThreshold) {
      if (diff > 0 && this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      } else if (diff < 0 && this.currentIndex < this.mediaCount - 1) {
        this.goToSlide(this.currentIndex + 1);
      } else {
        this._updateTrackPosition(true);
      }
    } else {
      this._updateTrackPosition(true);
    }

    this.isSwiping = false;
    this.swipeDirection = null;
  }

  // ===================
  // MOUSE HANDLING
  // ===================
  _onMouseDown(e) {
    if (e.button !== 0) return;

    this.startX = e.clientX;
    this.currentX = e.clientX;
    this.isSwiping = false;

    const onMouseMove = (e) => {
      const diff = e.clientX - this.startX;
      if (Math.abs(diff) > 5) {
        this.isSwiping = true;
        this.track.classList.add('is-dragging');
      }

      if (this.isSwiping) {
        e.preventDefault();
        this.currentX = e.clientX;

        const baseOffset = -this.currentIndex * 100;
        let dragPercent = (diff / this.track.offsetWidth) * 100;

        const atStart = this.currentIndex === 0 && diff > 0;
        const atEnd = this.currentIndex === this.mediaCount - 1 && diff < 0;

        if (atStart || atEnd) {
          dragPercent = dragPercent * 0.2;
        }

        this.track.style.transform = `translateX(${baseOffset + dragPercent}%)`;
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      this.track.classList.remove('is-dragging');

      if (!this.isSwiping) return;

      const diff = this.currentX - this.startX;

      if (Math.abs(diff) > this.dragThreshold) {
        if (diff > 0 && this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1);
        } else if (diff < 0 && this.currentIndex < this.mediaCount - 1) {
          this.goToSlide(this.currentIndex + 1);
        } else {
          this._updateTrackPosition(true);
        }
      } else {
        this._updateTrackPosition(true);
      }

      this.isSwiping = false;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  // ===================
  // KEYBOARD
  // ===================
  _onKeyDown(e) {
    if (e.target.closest('input, textarea, select')) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.goToSlide(this.currentIndex - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.goToSlide(this.currentIndex + 1);
        break;
    }
  }

  // ===================
  // NAVIGATION
  // ===================
  goToSlide(index) {
    const newIndex = Math.max(0, Math.min(index, this.mediaCount - 1));
    if (newIndex === this.currentIndex) return;

    this._pauseMedia(this.currentIndex);
    this.currentIndex = newIndex;
    this._updateTrackPosition(true);
    this._updateActiveStates();
    this._announceSlide();
  }

  _updateTrackPosition(animate) {
    const offset = -this.currentIndex * 100;

    if (!animate || this.prefersReducedMotion) {
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${offset}%)`;
      this.track.offsetHeight; // Force reflow
      this.track.style.transition = '';
    } else {
      this.track.style.transition = '';
      this.track.style.transform = `translateX(${offset}%)`;
    }
  }

  _updateActiveStates(skipScroll = false) {
    this.slides.forEach((slide, i) => {
      const isActive = i === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', !isActive);
    });

    this.filmstripThumbs.forEach((thumb, i) => {
      const isActive = i === this.currentIndex;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-selected', isActive);
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive);
    });

    this.miniThumbs.forEach((thumb, i) => {
      const isActive = i === this.currentIndex;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-selected', isActive);
    });

    // Only scroll thumbnails into view when navigating, not on init
    if (!skipScroll) {
      this._scrollFilmstripToActive();
      this._scrollMiniThumbToActive();
    }
  }

  _scrollFilmstripToActive() {
    if (!this.filmstripTrack || !this.filmstripThumbs[this.currentIndex]) return;
    const thumb = this.filmstripThumbs[this.currentIndex];
    const track = this.filmstripTrack;

    // Scroll only within the filmstrip container, not the page
    const thumbTop = thumb.offsetTop;
    const thumbHeight = thumb.offsetHeight;
    const trackHeight = track.clientHeight;
    const scrollTarget = thumbTop - (trackHeight / 2) + (thumbHeight / 2);

    track.scrollTo({ top: scrollTarget, behavior: 'smooth' });
  }

  _scrollMiniThumbToActive() {
    const container = this.container.querySelector('.product-carousel__mini-thumbs');
    if (!container || !this.miniThumbs[this.currentIndex]) return;
    const thumb = this.miniThumbs[this.currentIndex];

    // Scroll only within the mini-thumbs container, not the page
    const thumbLeft = thumb.offsetLeft;
    const thumbWidth = thumb.offsetWidth;
    const containerWidth = container.clientWidth;
    const scrollTarget = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

    container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
  }

  _announceSlide() {
    if (this.liveRegion) {
      this.liveRegion.textContent = `Image ${this.currentIndex + 1} of ${this.mediaCount}`;
    }
  }

  // ===================
  // VARIANT HANDLING
  // ===================
  _onVariantChange(e) {
    const variantId = e.detail?.variant?.id;
    if (!variantId) return;

    const mediaId = this.config.variantMediaMap[variantId];
    if (!mediaId) return;

    const slideIndex = this.slides.findIndex(
      slide => parseInt(slide.dataset.mediaId) === mediaId
    );

    if (slideIndex > -1 && slideIndex !== this.currentIndex) {
      this.goToSlide(slideIndex);
    }
  }

  _checkUrlVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    if (!variantId) return;

    const mediaId = this.config.variantMediaMap[variantId];
    if (!mediaId) return;

    const slideIndex = this.slides.findIndex(
      slide => parseInt(slide.dataset.mediaId) === mediaId
    );

    if (slideIndex > -1) {
      this.currentIndex = slideIndex;
    }
  }

  // ===================
  // VIDEO/YOUTUBE
  // ===================
  _setupVideos() {
    this.slides.forEach(slide => {
      const video = slide.querySelector('video');
      if (video) video.preload = 'none';
    });
  }

  _playYouTube(button) {
    const wrapper = button.closest('.product-carousel__youtube-wrapper');
    const youtubeId = wrapper.dataset.youtubeId;
    if (!youtubeId) return;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    wrapper.appendChild(iframe);
    wrapper.classList.add('is-playing');
  }

  _pauseMedia(index) {
    const slide = this.slides[index];
    if (!slide) return;

    const video = slide.querySelector('video');
    if (video && !video.paused) video.pause();

    const youtubeWrapper = slide.querySelector('.product-carousel__youtube-wrapper');
    if (youtubeWrapper) {
      const iframe = youtubeWrapper.querySelector('iframe');
      if (iframe) {
        iframe.remove();
        youtubeWrapper.classList.remove('is-playing');
      }
    }
  }

  // ===================
  // UTILITIES
  // ===================
  _checkFilmstripOverflow() {
    if (!this.filmstrip || !this.filmstripTrack) return;
    const hasOverflow = this.filmstripTrack.scrollHeight > this.filmstripTrack.clientHeight;
    this.filmstrip.classList.toggle('has-overflow', hasOverflow);
  }

  _debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  destroy() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
    this.slides.forEach((_, i) => this._pauseMedia(i));
  }
}

// Initialize
(function() {
  const instances = new Map();

  function init() {
    document.querySelectorAll('.product-carousel').forEach(container => {
      const id = container.dataset.sectionId;
      if (instances.has(id)) instances.get(id).destroy();
      instances.set(id, new ProductCarousel(container));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('.product-carousel');
    if (container) {
      const id = container.dataset.sectionId;
      if (instances.has(id)) instances.get(id).destroy();
      instances.set(id, new ProductCarousel(container));
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const container = e.target.querySelector('.product-carousel');
    if (container && instances.has(container.dataset.sectionId)) {
      instances.get(container.dataset.sectionId).destroy();
      instances.delete(container.dataset.sectionId);
    }
  });

  window.ProductCarousel = ProductCarousel;
})();
